import { Badge, Banner, Button, Dialog, LinkButton } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataTable as Table } from '../components/ui/DataTable';
import { useTenant } from '../contexts/TenantContext';
import { useAppToast } from '../hooks/useAppToast';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { FEATURES, SHOWCASE_ORDER, featureTitle } from '../lib/planCatalog';
import type { PlanFeature } from '../contexts/PlanContext';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  features: string[];
  aiDailyRequests: number | null;
  description: string | null;
  monthlyPrice: number;
  maxUsers: number;
  maxContacts: number;
  maxCampaigns: number;
  maxConnections: number;
  purchasable: boolean;
}

interface Invoice {
  id: string;
  number: string | null;
  createdAt: string;
  dueDate: string | null;
  amount: number;
  status: string;
  hostedUrl: string | null;
  pdfUrl: string | null;
}

interface Overview {
  stripeConfigured: boolean;
  stripeError: string | null;
  plan: Plan | null;
  planStatus: string;
  trialEndsAt: string | null;
  billingMethod: 'CARD' | 'PIX' | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasSubscription: boolean;
  /** Fim do prazo de arrependimento (7 dias do início da assinatura) */
  withdrawalUntil: string | null;
  paymentMethod: { brand: string; last4: string; expMonth: number; expYear: number } | null;
  usage: { users: number; contacts: number; campaigns: number; connections: number };
  limits: { users: number; contacts: number; campaigns: number; connections: number } | null;
  plans: Plan[];
  invoices: Invoice[];
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('pt-BR')} ${n === 1 ? one : many}`;
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const date = (v: string | null) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');

const STATUS: Record<string, { label: string; variant: 'green' | 'yellow' | 'red' | 'lightgray' | 'blue' }> = {
  ACTIVE: { label: 'Ativo', variant: 'green' },
  TRIAL: { label: 'Avaliação', variant: 'blue' },
  PAST_DUE: { label: 'Pagamento pendente', variant: 'red' },
  SUSPENDED: { label: 'Suspenso', variant: 'red' },
  CANCELLED: { label: 'Cancelado', variant: 'lightgray' },
};

const INVOICE_STATUS: Record<string, { label: string; variant: 'green' | 'yellow' | 'red' | 'lightgray' }> = {
  paid: { label: 'Paga', variant: 'green' },
  open: { label: 'Em aberto', variant: 'yellow' },
  uncollectible: { label: 'Não paga', variant: 'red' },
  void: { label: 'Cancelada', variant: 'lightgray' },
};

/** Cores do `Badge` do Cloudscape: `lightgray` → `grey`, `yellow` → `severity-medium`. */
const BADGE_COLOR: Record<string, 'green' | 'blue' | 'red' | 'orange' | 'neutral'> = {
  green: 'green', blue: 'blue', red: 'red', yellow: 'orange', lightgray: 'neutral',
};

const USAGE_LABELS: Array<[keyof Overview['usage'], string]> = [
  ['users', 'Membros'],
  ['contacts', 'Contatos'],
  ['campaigns', 'Campanhas interativas'],
  ['connections', 'Conexões'],
];

/** Faturamento da organização (como o "Billing" do MongoDB Atlas) */
export function FaturamentoPage() {
  const { can, organizations, selectedTenantId, selectedTenant } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?recurso= vem da tela de recurso bloqueado: destaca os planos que o incluem
  const wanted = (searchParams.get('recurso') || '') as PlanFeature | '';
  const [data, setData] = useState<Overview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<Plan | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState<'sucesso' | 'cancelado' | null>(null);

  const canManage = can('billing:manage');
  const orgName = organizations.find((o) => o.id === selectedTenantId)?.name || selectedTenant?.name || 'Organização';

  const load = useCallback(async () => {
    try {
      const res = await fetchApi('/api/billing');
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Erro ao carregar o faturamento');
      setData(body);
      return body as Overview;
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Erro ao carregar o faturamento');
      return null;
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  // Volta do Checkout: o webhook ativa a assinatura em segundos; recarrega algumas vezes
  useEffect(() => {
    const result = searchParams.get('checkout');
    if (result !== 'sucesso' && result !== 'cancelado') return;
    setCheckoutResult(result);
    searchParams.delete('checkout');
    setSearchParams(searchParams, { replace: true });
    if (result !== 'sucesso') return;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      const d = await load();
      if ((d?.hasSubscription && d.planStatus === 'ACTIVE') || tries >= 8) clearInterval(timer);
    }, 2500);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const post = async (key: string, url: string, body?: unknown) => {
    setBusy(key);
    try {
      const res = await fetchApi(url, { method: 'POST', body: JSON.stringify(body ?? {}) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Não foi possível concluir');
      return json;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível concluir');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const subscribe = async (plan: Plan, method: 'card' | 'pix') => {
    const result = await post(`${plan.id}:${method}`, '/api/billing/checkout', { planId: plan.id, method });
    if (!result) return;
    setChoosing(null);
    if (result.url) {
      window.location.assign(result.url);
      return;
    }
    toast.success(result.message || 'Assinatura atualizada');
    load();
  };

  const openPortal = async () => {
    const result = await post('portal', '/api/billing/portal');
    if (result?.url) window.location.assign(result.url);
  };

  const cancelOrResume = async (resume: boolean) => {
    setConfirmCancel(false);
    const result = await post('cancel', '/api/billing/cancel', { resume });
    if (!result) return;
    toast.success(resume ? 'Assinatura retomada' : 'A assinatura será encerrada no fim do período');
    load();
  };

  // Direito de arrependimento (CDC, art. 49): cancela na hora e devolve o que foi pago
  const withdraw = async () => {
    setConfirmWithdraw(false);
    const result = await post('withdraw', '/api/billing/withdraw');
    if (!result) return;
    toast.success(`Assinatura cancelada. ${(result.refundedCents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} devolvidos.`);
    load();
  };

  if (loadError) {
    return <div className="page-content" style={{ paddingTop: 28 }}><Banner variant="error" description={<>{loadError}</>} /></div>;
  }
  if (!data) {
    return (
      <div className="py-8"><SkeletonText lines={4} /></div>
    );
  }

  const status = STATUS[data.planStatus] || { label: data.planStatus, variant: 'lightgray' as const };
  const currentPlanId = data.hasSubscription || data.planStatus === 'ACTIVE' ? data.plan?.id : null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Faturamento</h2>
          <div style={{ color: 'var(--text-muted)' }}>Plano, forma de pagamento e faturas de <strong>{orgName}</strong>.</div>
        </div>
        {canManage && data.stripeConfigured && data.hasSubscription && (
          <Button variant="secondary" icon={ICONS.CreditCard} loading={busy === 'portal'} onClick={openPortal}>
            Gerenciar pagamento
          </Button>
        )}
      </div>

      <div className="page-content space-y-6">
        {checkoutResult === 'sucesso' && (
          <Banner variant="default" description={<>
            Pagamento enviado. A assinatura é ativada assim que o Stripe confirmar (alguns segundos).
          </>} />
        )}
        {checkoutResult === 'cancelado' && (
          <Banner variant="default" description={<>O pagamento foi cancelado; nada foi cobrado.</>} />
        )}
        {!data.stripeConfigured && (
          <Banner variant="default" description={<>A cobrança online ainda não foi ativada na plataforma. Para mudar de plano, fale com o suporte.</>} />
        )}
        {data.stripeError && <Banner variant="alert" description={<>Não foi possível consultar o Stripe agora: {data.stripeError}</>} />}
        {data.planStatus === 'PAST_DUE' && (
          <Banner variant="error" description={<>
            O último pagamento não foi aprovado. {data.billingMethod === 'PIX' ? 'Pague a fatura em aberto abaixo' : 'Atualize o cartão em Gerenciar pagamento'} para evitar a suspensão.
          </>} />
        )}
        {canManage && data.withdrawalUntil && (
          <Banner variant="default" description={<>
            Você está no prazo de arrependimento até {date(data.withdrawalUntil)} (Código de Defesa do Consumidor, art. 49): pode desistir e receber de volta o que pagou.{' '}
            <button type="button" className="project-link" onClick={() => setConfirmWithdraw(true)}>Desistir e reembolsar</button>
          </>} />
        )}
        {data.cancelAtPeriodEnd && (
          <Banner variant="alert" description={<>
            A assinatura termina em {date(data.currentPeriodEnd)}.{' '}
            {canManage && <button type="button" className="project-link" onClick={() => cancelOrResume(true)}>Retomar assinatura</button>}
          </>} />
        )}

        {/* Resumo */}
        <div className="billing-summary">
          <section className="billing-card">
            <div className="billing-card__label">Plano atual</div>
            <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
              <h3 className="text-lg font-semibold">{data.plan?.displayName || 'Sem plano'}</h3>
              <Badge variant={BADGE_COLOR[status.variant]}>{status.label}</Badge>
            </div>
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
              {data.plan
                ? `${brl(data.plan.monthlyPrice)} por mês`
                : data.trialEndsAt ? `Avaliação até ${date(data.trialEndsAt)}` : 'Escolha um plano abaixo'}
            </div>
            {canManage && data.hasSubscription && !data.cancelAtPeriodEnd && (
              <button type="button" className="billing-card__link" onClick={() => setConfirmCancel(true)}>Cancelar assinatura</button>
            )}
          </section>

          <section className="billing-card">
            <div className="billing-card__label">{data.cancelAtPeriodEnd ? 'Termina em' : 'Próxima cobrança'}</div>
            <h3 className="text-lg font-semibold" style={{ marginTop: 4 }}>{date(data.currentPeriodEnd)}</h3>
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
              {data.billingMethod === 'PIX'
                ? 'Pix — fatura mensal'
                : data.paymentMethod
                  ? `Cartão ${data.paymentMethod.brand.toUpperCase()} •••• ${data.paymentMethod.last4} (${String(data.paymentMethod.expMonth).padStart(2, '0')}/${data.paymentMethod.expYear})`
                  : 'Sem forma de pagamento'}
            </div>
          </section>

          <section className="billing-card">
            <div className="billing-card__label">Uso da organização</div>
            <div className="space-y-2" style={{ marginTop: 8 }}>
              {USAGE_LABELS.map(([key, label]) => {
                const used = data.usage[key];
                const max = data.limits?.[key];
                const pct = max ? Math.min(100, Math.round((used / max) * 100)) : 0;
                return (
                  <div key={key}>
                    <div className="flex justify-between" style={{ fontSize: 13 }}>
                      <span>{label}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{used.toLocaleString('pt-BR')}{max ? ` de ${max.toLocaleString('pt-BR')}` : ''}</span>
                    </div>
                    {max ? (
                      <div className="billing-meter" role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                        <div className={`billing-meter__fill ${pct >= 90 ? 'billing-meter__fill--full' : ''}`} style={{ width: `${pct}%` }} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* Planos */}
        {data.plans.length > 0 && (
          <section>
            <h4 className="text-base font-semibold" style={{ marginBottom: 12 }}>Planos</h4>
            {wanted && FEATURES[wanted] && (
              <div className="mb-4">
                <Banner variant="default" description={<>
                  <strong>{FEATURES[wanted].title}</strong>: {FEATURES[wanted].pitch} Os planos destacados incluem este recurso.
                </>} />
              </div>
            )}
            <div className="billing-plans">
              {data.plans.map((p, i) => {
                const isCurrent = p.id === currentPlanId;
                const prev = i > 0 ? data.plans[i - 1] : null;
                // o que este plano acrescenta ao anterior (vitrine "tudo do X, mais:")
                const added = SHOWCASE_ORDER.filter((f) => p.features.includes(f) && !(prev?.features ?? []).includes(f));
                const hasWanted = !!wanted && p.features.includes(wanted);
                return (
                  <div key={p.id} className={`billing-plan ${isCurrent ? 'billing-plan--current' : ''} ${hasWanted && !isCurrent ? 'billing-plan--wanted' : ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold" style={{ fontSize: 16 }}>{p.displayName}</div>
                      {isCurrent ? <Badge variant="green">Atual</Badge> : p.name === 'PROFISSIONAL' ? <Badge variant="blue">Mais escolhido</Badge> : null}
                    </div>
                    <div className="billing-plan__price">{brl(p.monthlyPrice)}<span>/mês</span></div>
                    {p.description && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{p.description}</div>}
                    <ul className="billing-plan__limits">
                      <li>{plural(p.maxUsers, 'membro', 'membros')}</li>
                      <li>{plural(p.maxContacts, 'contato', 'contatos')}</li>
                      <li>{plural(p.maxCampaigns, 'campanha interativa', 'campanhas interativas')}</li>
                      <li>{plural(p.maxConnections, 'conexão de WhatsApp', 'conexões de WhatsApp')}</li>
                      {p.features.includes('ai') && p.aiDailyRequests ? <li>{p.aiDailyRequests.toLocaleString('pt-BR')} pedidos de IA por dia</li> : null}
                    </ul>
                    {added.length > 0 && (
                      <div className="billing-plan__features">
                        <div className="billing-plan__features-title">{prev ? `Tudo do ${prev.displayName}, mais:` : 'Inclui:'}</div>
                        <ul>
                          {added.map((f) => (
                            <li key={f} className={f === wanted ? 'billing-plan__feature--wanted' : ''} title={FEATURES[f].pitch}>{featureTitle(f)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {canManage && !isCurrent && (
                      <Button variant="secondary" disabled={!data.stripeConfigured || !p.purchasable} loading={busy?.startsWith(p.id)} onClick={() => (data.hasSubscription ? subscribe(p, 'card') : setChoosing(p))}>
                        {data.hasSubscription ? 'Mudar para este plano' : 'Assinar'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Faturas */}
        <section>
          <h4 className="text-base font-semibold" style={{ marginBottom: 12 }}>Faturas</h4>
          <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden">
            {data.invoices.length === 0 ? (
              <div className="text-center py-10"><div className="!text-[var(--text-faint)]">Nenhuma fatura ainda.</div></div>
            ) : (
              <div className="overflow-x-auto">
                <Table
                  items={data.invoices}
                  trackBy={(inv) => inv.id}
                  columnDefinitions={[
                    {
                      id: 'fatura',
                      header: 'Fatura',
                      cell: (inv) => <div className="font-semibold">{inv.number || inv.id}</div>,
                    },
                    { id: 'emissao', header: 'Emissão', cell: (inv) => date(inv.createdAt) },
                    { id: 'vencimento', header: 'Vencimento', cell: (inv) => date(inv.dueDate) },
                    { id: 'valor', header: 'Valor', cell: (inv) => brl(inv.amount) },
                    {
                      id: 'situacao',
                      header: 'Situação',
                      cell: (inv) => {
                        const st = INVOICE_STATUS[inv.status] || { label: inv.status, variant: 'lightgray' as const };
                        return <Badge variant={BADGE_COLOR[st.variant]}>{st.label}</Badge>;
                      },
                    },
                    {
                      id: 'acoes',
                      header: 'Ações',
                      cell: (inv) => (
                        <div className="flex gap-2">
                          {inv.hostedUrl && (
                            <LinkButton variant="secondary" href={inv.hostedUrl} target="_blank" rel="noopener noreferrer">
                              {inv.status === 'open' ? 'Pagar' : 'Ver'}
                            </LinkButton>
                          )}
                          {inv.pdfUrl && (
                            <LinkButton variant="secondary" icon={ICONS.Download} href={inv.pdfUrl} target="_blank" rel="noopener noreferrer">
                              PDF
                            </LinkButton>
                          )}
                        </div>
                      ),
                    },
                  ]}
                  ariaLabels={{ tableLabel: 'Faturas da organização' }}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Escolher forma de pagamento */}
      <Dialog.Root open={choosing !== null} onOpenChange={(o: boolean) => { if (!o) (() => setChoosing(null))(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Assinar o {choosing?.displayName}</h3>}</Dialog.Title>

        <div style={{ color: 'var(--text-muted)', marginBottom: 20 }}>{choosing ? `${brl(choosing.monthlyPrice)} por mês. ` : ''}Escolha como pagar:</div>
        <div className="billing-methods">
          <button type="button" className="billing-method" disabled={!!busy} onClick={() => choosing && subscribe(choosing, 'card')}>
            <ICONS.CreditCard size={32} />
            <span>
              <strong>Cartão de crédito</strong>
              <small>Cobrança automática todo mês</small>
            </span>
            {busy === `${choosing?.id}:card` && <span className="billing-method__spinner" aria-hidden />}
          </button>
          <button type="button" className="billing-method" disabled={!!busy} onClick={() => choosing && subscribe(choosing, 'pix')}>
            <ICONS.LightningBolt size={32} />
            <span>
              <strong>Pix</strong>
              <small>Fatura mensal paga pelo QR Code (vence em 3 dias)</small>
            </span>
            {busy === `${choosing?.id}:pix` && <span className="billing-method__spinner" aria-hidden />}
          </button>
        </div>
        <div style={{ color: 'var(--text-faint)', fontSize: 12, marginTop: 16 }}>O pagamento é processado pelo Stripe. Você pode cancelar quando quiser.</div>
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal
        open={confirmWithdraw}
        title="Desistir da assinatura"
        confirmText="Desistir e reembolsar"
        requiredText="REEMBOLSAR"
        onConfirm={withdraw}
        onCancel={() => setConfirmWithdraw(false)}
      >
        A assinatura é cancelada agora, o acesso do plano termina e o valor pago volta para o cartão ou a conta usada no Pix (o banco pode levar até 10 dias úteis).
      </DangerConfirmModal>

      <DangerConfirmModal
        open={confirmCancel}
        title="Cancelar assinatura"
        confirmText="Cancelar assinatura"
        requiredText="CANCELAR"
        onConfirm={() => cancelOrResume(false)}
        onCancel={() => setConfirmCancel(false)}
      >
        A assinatura continua ativa até {date(data.currentPeriodEnd)} e não é renovada. Você pode retomá-la antes disso.
      </DangerConfirmModal>
    </>
  );
}
